/**
 * Hook to monitor session state and trigger expiry handling.
 *
 * Three layers of protection:
 *   1. Dynamic setTimeout — schedules a check ~1 min before token expiry.
 *   2. Fallback setInterval — every 5 min in case the setTimeout is lost.
 *   3. visibilitychange listener — fires immediately when user returns to a
 *      backgrounded tab (browsers throttle timers in hidden tabs, so the
 *      setTimeout/setInterval may be significantly delayed).
 *
 * Actual teardown + redirect to /login is owned by sessionManager — this hook
 * only decides *when* to call it.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { sessionManager } from '../services/sessionManager';
import { tokenRefresher } from '../services/tokenRefresher';
import { isAuthEntryPath } from '../config/authRoutes';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('SESSION_MONITOR');

export const useSessionMonitor = (): void => {
  const { isAuthenticated } = useAuth();
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiry = async () => {
      if (isAuthEntryPath(window.location.pathname)) {
        return;
      }

      const authToken = localStorage.getItem('authToken');
      const idToken = localStorage.getItem('id_token');
      const token = authToken || idToken;

      if (!token) {
        log.warn('No token found during expiry check');
        return;
      }

      if (sessionManager.isTokenExpired(token)) {
        log.warn('Token expired — attempting silent refresh before signing out');
        const newToken = await tokenRefresher.tryRefresh();
        if (newToken) {
          log.log('Silent refresh succeeded — session preserved');
          scheduleNextCheck(newToken);
          return;
        }
        log.warn('Silent refresh failed — triggering session expired');
        sessionManager.handleSessionExpired(token);
      } else {
        scheduleNextCheck(token);
      }
    };

    const scheduleNextCheck = (token: string) => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      const timeUntilExpiry = sessionManager.getTimeUntilExpiry(token);
      if (timeUntilExpiry) {
        const checkIn = Math.max(Math.min(timeUntilExpiry - 60_000, 60_000), 5_000);
        expiryTimerRef.current = setTimeout(checkTokenExpiry, checkIn);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        log.log('Tab became visible — checking token expiry');
        checkTokenExpiry();
      }
    };

    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);
};
